import {
  Button,
  Card,
  Col,
  Icons,
  notification,
  Row,
  Show,
  Space,
  Table,
  Tooltip,
  Typography,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { Link } from '@pankod/refine-react-router-v6';
import { CustomDatePicker, Search } from 'components';
import { IAccountLog, IAccountLogInfo } from 'interfaces';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccountLogs } from 'services/account';
import { getPaymentReceipt } from 'services/payment';
import { EInvoiceType, getSaleInvoice } from 'services/sale';
import { ECurrency, getCurrencySymbol } from 'utilities/currency';
import MetaData from 'utilities/metadata';
import { capitalize, previewPdfFromBase64 } from 'utilities/shared';
import {
  ETransactionType,
  EUserType,
  IAccountLogWithBalance,
  IOptionGroup,
  ISearchObject,
  SearchKeywords,
} from './logs.type';
import styles from './style.module.css';

const { Text, Title } = Typography;

export const AccountLogs = () => {
  const [accountLogInfo, setAccountLogInfo] = useState<IAccountLogInfo>();
  const [keywords, setKeywords] = useState<string>('{}');
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const year = new Date().getFullYear();
    return [new Date(year, 0, 1, 0, 0, 0), new Date(year, 11, 31, 23, 59, 59)];
  });

  const pathname = window.location.pathname.split('/');
  const accountLogsId = pathname[pathname.length - 1];
  const userType = capitalize(pathname[pathname.length - 2]) as EUserType;

  const [start, end] = dateRange;
  const dateQuery = useMemo(() => {
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [start, end]);

  const fetchDataOnMountCallback = useCallback(
    async (values?: [Date, Date]) => {
      setIsLoading(true);
      try {
        if (values) {
          setDateRange(values);
        }
        const accountLogsResponse = await getAccountLogs({ ...dateQuery, userType, accountLogsId });
        const accountLogInfoData = accountLogsResponse.data;
        const keywordsData = accountLogsResponse.keywords;
        setAccountLogInfo(accountLogInfoData);
        setKeywords(keywordsData);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
      }
      setIsLoading(false);
    },
    [dateQuery, userType, accountLogsId]
  );

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  // const { RangePicker } = DatePicker;
  // const disabledDate = (date: Dayjs) => date > dayjs();
  const username = accountLogInfo?.name;
  const currentBalance = accountLogInfo?.balance;
  const currencySymbol = getCurrencySymbol(ECurrency.BDT);
  const accountLogs = accountLogInfo?.logs ?? [];

  const renderTitle = (title: string) => (
    <div style={{ fontWeight: 'bold' }}>
      <Text style={{ fontSize: '1rem' }}>{title}</Text>
    </div>
  );

  const renderItem = (id: string, title: string) => ({
    value: title,
    label: (
      <div key={id}>
        <Text style={{ marginLeft: '1rem' }}>{title}</Text>
      </div>
    ),
  });

  const getOptionGroup = (options: string[]) => {
    const optionGroup: IOptionGroup[] = [];

    for (let [idx, value] of options.entries()) {
      if (!value) {
        continue;
      }
      optionGroup.push(renderItem(`${idx}`, value));
    }

    return optionGroup;
  };

  const getOptions = () => {
    const parsedKeywords: SearchKeywords = JSON.parse(keywords);
    const clientTransactionOptions = [
      ETransactionType.Sale,
      ETransactionType.PaymentReceived,
      ETransactionType.Return,
      ETransactionType.Refund,
    ];
    const supplierTransactionOptions = [
      ETransactionType.Purchase,
      ETransactionType.PaymentReceived,
    ];
    let invoiceIdOptionGroup: IOptionGroup[] = [];
    let transactionTypeOptionGroup: IOptionGroup[] = [];

    if (userType === EUserType.Client) {
      transactionTypeOptionGroup = clientTransactionOptions.map((item, idx) =>
        renderItem(`${idx}`, item)
      );
    }
    if (userType === EUserType.Supplier) {
      transactionTypeOptionGroup = supplierTransactionOptions.map((item, idx) =>
        renderItem(`${idx}`, item)
      );
    }

    if (Object.keys(parsedKeywords).length) {
      invoiceIdOptionGroup = getOptionGroup(parsedKeywords.id);
    }

    return [
      {
        label: renderTitle('Invoice ID'),
        options: invoiceIdOptionGroup,
      },
      {
        label: renderTitle('Transaction Type'),
        options: transactionTypeOptionGroup,
      },
    ];
  };

  const onSelectOption = async (searchText: string | ETransactionType) => {
    setIsLoading(true);
    try {
      const searchObject: ISearchObject = { query: undefined, transactionType: undefined };
      if (
        ETransactionType.Sale === searchText ||
        ETransactionType.Purchase === searchText ||
        ETransactionType.PaymentReceived === searchText ||
        ETransactionType.Return === searchText ||
        ETransactionType.Refund === searchText
      ) {
        searchObject.transactionType = searchText;
      } else {
        searchObject.query = searchText;
      }

      const accountLogsResponse = await getAccountLogs({
        ...dateQuery,
        ...searchObject,
        userType,
        accountLogsId,
      });
      const accountLogInfoData = accountLogsResponse.data;
      const keywordsData = accountLogsResponse.keywords;
      setAccountLogInfo(accountLogInfoData);
      if (keywordsData) {
        setKeywords(keywordsData);
      }
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  };

  const supplierDebitOrCreditCheck = window.location.pathname.includes('supplier')
    ? 'Debit'
    : 'Credit';
  const clientDebitOrCreditCheck = window.location.pathname.includes('client') ? 'Debit' : 'Credit';

  return (
    <Show
      title={`${userType} - ${username ?? ''}`}
      isLoading={isLoading}
      headerProps={{ extra: null }}
    >
      <MetaData />
      {/* Search bar */}
      <Search
        placeholder="Search by Invoice ID, Transaction Type"
        size="large"
        className={styles.search}
        options={getOptions()}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMountCallback}
      />

      {/* Date Picker */}
      <Row justify="space-around" align="middle" style={{ marginBottom: '2rem' }}>
        <Col>
          <div
            style={{
              padding: '0.5rem',
              borderRadius: '2px',
            }}
          >
            <Text style={{ fontSize: '1.2rem' }}>Show data from</Text>
            <CustomDatePicker
              className={styles['date-picker']}
              value={dateRange}
              onChange={fetchDataOnMountCallback}
            />
          </div>
        </Col>
      </Row>

      {/* Balance info */}
      <Row justify="space-around" align="middle" style={{ marginBottom: '2rem' }}>
        <Col>
          <Card bordered={false} style={{ textAlign: 'center' }}>
            <Title level={4} style={{ display: 'inline-block', marginRight: '0.5rem' }}>
              Current balance:
            </Title>
            <Text strong style={{ fontSize: '1.2rem' }}>
              <span style={{ fontWeight: 'bolder' }}>{currencySymbol}</span> {currentBalance}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Logs table */}
      <Row gutter={[32, 32]}>
        <Col lg={24} md={24}>
          <Table
            dataSource={accountLogs}
            loading={isLoading}
            rowKey={(log, idx) => `${log.id}-${idx}`}
            scroll={{ x: true }}
            pagination={{
              position: ['bottomCenter'],
            }}
          >
            <Table.Column<IAccountLogWithBalance>
              align="center"
              dataIndex="id"
              title="Document ID"
              render={(_value, record) => (
                <AccountLogsTableIdColumn record={record} userType={userType} />
              )}
            />
            <Table.Column dataIndex="warehouse" title="Warehouse" />
            <Table.Column<IAccountLogWithBalance>
              dataIndex="date"
              title="Date"
              render={(_, record): React.ReactNode => {
                const date = new Date(record.date).toLocaleDateString('en-GB');
                return <Text>{date}</Text>;
              }}
            />
            <Table.Column dataIndex="transactionType" title="Transaction Type" />
            <Table.Column dataIndex="invoice" title={clientDebitOrCreditCheck} />
            <Table.Column dataIndex="received" title={supplierDebitOrCreditCheck} />
            <Table.Column dataIndex="runningBalance" title="Account Balance" />
            <Table.Column<IAccountLogWithBalance>
              title="Actions"
              dataIndex="actions"
              key="actions"
              render={(_, record) => {
                const userAndBankInfo = (
                  <span>
                    User: {record.user}
                    <br /> Receiving Bank: {record.receivingBank}
                  </span>
                );
                const userInfo = <span>User: {record.user}</span>;
                const text = record.receivingBank && record.user ? userAndBankInfo : userInfo;
                return (
                  <Space>
                    <Tooltip title={text}>
                      <Button size="small" icon={<Icons.InfoOutlined />} />
                    </Tooltip>
                  </Space>
                );
              }}
            />
          </Table>
        </Col>
      </Row>
    </Show>
  );
};

type AccountLogsTableIdColumnProps = {
  record: IAccountLog;
  userType: EUserType;
};

const AccountLogsTableIdColumn: React.FC<AccountLogsTableIdColumnProps> = ({
  record,
  userType,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { push } = useNavigation();

  const isInvoiceButton = useMemo(
    () => record.transactionType === 'Sale' || record.transactionType === 'Payment Received',
    [record.transactionType]
  );

  const generateInvoice = useCallback(async (saleId: string) => {
    try {
      setIsLoading(true);
      const invoiceString = await getSaleInvoice(saleId, EInvoiceType.Sales);
      previewPdfFromBase64(invoiceString);
    } catch {
      notification.error({
        message: 'Failed',
        description: 'Failed to download invoice',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateReceipt = useCallback(
    async (saleId: string) => {
      try {
        setIsLoading(true);
        const invoiceString = await getPaymentReceipt(saleId, userType, record.runningBalance);
        previewPdfFromBase64(invoiceString);
      } catch {
        notification.error({
          message: 'Failed',
          description: 'Failed to download payment receipt',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [record.runningBalance, userType]
  );

  const handleClick = useCallback(() => {
    const { id, transactionType } = record;

    if (transactionType === 'Purchase') return push(`/purchases/show/${id}`);
    if (transactionType === 'Return') return push(`/returns/show/${id}`);
    if (transactionType === 'Sale') return generateInvoice(id);
    if (transactionType === 'Payment Received') return generateReceipt(id);
  }, [record, push, generateInvoice, generateReceipt]);

  return (
    <Text style={{ cursor: 'pointer' }} onClick={handleClick}>
      {isInvoiceButton ? (
        <Button loading={isLoading} disabled={isLoading}>
          {record.id}
        </Button>
      ) : (
        <Link to="">{record.id}</Link>
      )}
    </Text>
  );
};
